import { JsStack } from '@/components/JSStack'
import toastConfig from '@/config/toast/toast-config'
import useOTAUpdates from '@/hooks/useOTAUpdates'
import { initSentry } from '@/lib/sentry'
import Providers from '@/providers'
import heapService from '@/services/heap'
import { useAuthStore, useStore } from '@/store'
import { useSessionStore } from '@/store'
import { PortalHost } from '@/ui/primitives/portal'
import { TransitionPresets } from '@react-navigation/stack'
import * as Sentry from '@sentry/react-native'
import { getUnixTime } from 'date-fns'
import Constants, { ExecutionEnvironment } from 'expo-constants'
import { useFonts } from 'expo-font'
import { Stack, useNavigationContainerRef } from 'expo-router'
import * as SplashScreen from 'expo-splash-screen'
import { useEffect, useState } from 'react'
import Toast from 'react-native-toast-message'
import { firebaseAuth } from '#/firebase'
import '#/global.css'

SplashScreen.preventAutoHideAsync()

const navigationIntegration = Sentry.reactNavigationIntegration({
	enableTimeToInitialDisplay:
		Constants.executionEnvironment === ExecutionEnvironment.StoreClient,
})

initSentry({ integrations: [navigationIntegration] })

function RootLayout() {
	const [isAuthReady, setIsAuthReady] = useState(false)
	const ref = useNavigationContainerRef()
	const { setLastOpenedAt } = useSessionStore()
	const hasHydrated = useStore((state) => state._hasHydratedFromStorage)
	const { authFromFirebaseUser, signOut } = useAuthStore()
	useOTAUpdates()

	const [fontsLoaded] = useFonts({
		'MaisonNeue-Bold': require('#/assets/fonts/MaisonNeue-Bold.otf'),
		'MaisonNeue-Demi': require('#/assets/fonts/MaisonNeue-Demi.otf'),
		'MaisonNeue-Light': require('#/assets/fonts/MaisonNeue-Light.otf'),
		'MaisonNeue-Medium': require('#/assets/fonts/MaisonNeue-Medium.otf'),
	})

	const onAuthStateReady = async () => {
		if (isAuthReady) {
			return
		}

		if (firebaseAuth.currentUser) {
			const authToken = await firebaseAuth.currentUser.getIdToken()
			const tokenResult = await firebaseAuth.currentUser.getIdTokenResult()

			authFromFirebaseUser(authToken, firebaseAuth.currentUser, tokenResult.claims)
			setIsAuthReady(true)
		} else {
			signOut()
			setIsAuthReady(true)
		}
	}

	useEffect(() => {
		if (ref) {
			navigationIntegration.registerNavigationContainer(ref)
		}
	}, [ref])

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		firebaseAuth.authStateReady().then(onAuthStateReady)
		heapService.bootstrap()
	}, [])

	useEffect(() => {
		if (hasHydrated) {
			setLastOpenedAt(getUnixTime(Date.now()))
		}
	}, [hasHydrated, setLastOpenedAt])

	useEffect(() => {
		if (hasHydrated && isAuthReady && fontsLoaded) {
			SplashScreen.hideAsync()
		}
	}, [hasHydrated, isAuthReady, fontsLoaded])

	if (!hasHydrated || !isAuthReady || !fontsLoaded) {
		return null
	}

	return <RootLayoutNav />
}

function RootLayoutNav() {
	return (
		<Providers>
			<JsStack screenOptions={{ headerShown: false }}>
				<Stack.Screen name="login" />
				<Stack.Screen name="verify-login" />
				<JsStack.Screen
					name="join"
					options={{
						...TransitionPresets.ModalPresentationIOS,
						headerShown: false,
						presentation: 'modal',
						gestureEnabled: true,
					}}
				/>
				<Stack.Screen name="index" />
				<Stack.Screen name="billing" />
				<Stack.Screen name="profile" />
				<Stack.Screen name="my-smile" />
				<JsStack.Screen
					name="schedule-appointment"
					options={{
						...TransitionPresets.ModalPresentationIOS,
						headerShown: false,
						presentation: 'modal',
						gestureEnabled: true,
					}}
				/>
				<Stack.Screen
					name="reschedule-perio-appointment"
					options={{
						headerShown: false,
						presentation: 'fullScreenModal',
					}}
				/>
			</JsStack>
			<Toast config={toastConfig} position="bottom" bottomOffset={0} keyboardOffset={0} />
			<PortalHost />
		</Providers>
	)
}

export default Sentry.wrap(RootLayout)
